import React from 'react';
import { Formik, useFormikContext, FormikConfig } from 'formik';
import TextareaAutosize from 'components/textautosize';
import TextInput, { TextInputProps } from 'components/text-input';
import withForm from 'components/form/hoc/with-form';
import FileInput from 'components/file-input';
import PSPDFKit from 'components/pspdf';
import FormButton from 'components/form/components/form-button';
import SubmitButton from 'components/form/components/submit-button';
import TextEditor from 'components/text-editor';
import Option from 'components/option';
import OptionGroup from 'components/option-group';
import { Header } from 'components/form/style';
import useEventListener from 'hooks/use-event-listener';
import { renderChildren } from 'utils/react';
import { uniqueId, isEmpty, get } from 'lodash';
import { FormikHelpers } from 'formik/dist/types';

export const FormContext = React.createContext({});

export type FormOnSubmit<Values = any> = (
  values: Values,
  formikHelpers: FormikHelpers<Values>,
) => void | Promise<any>;

type Props = FormikConfig<any> & { submitOnEnter?: boolean };

/*interface FormComponents {
  TextInput: React.ComponentType<TextInputProps>;
  TextEditor: React.ComponentType<TextInputProps>;
  FileInput: React.ComponentType<TextInputProps>;
}*/

function Form({ children, enableReinitialize = true, ...props }: Props) {
  const [context, setContext] = React.useState({});

  React.useEffect(() => {
    // We want to set form context only once when component mounts so that we don't updated all the form components when context changes
    const inputs = {};

    const formContext = {
      formId: uniqueId('form'),
      inputs,
      setInput: (name, ref) => {
        inputs[name] = ref;
      },
      getInput: (name) => get(inputs, `${name}.current`, {}),
    };

    setContext(formContext);
  }, []);

  if (isEmpty(context)) {
    return null;
  }

  return (
    <FormContext.Provider value={context}>
      <Formik
        enableReinitialize={enableReinitialize}
        validateOnChange={false}
        validateOnBlur={false}
        {...props}>
        {(form) => (
          <>
            {props.submitOnEnter && <SubmitOnEnter />}
            {renderChildren(children, form)}
          </>
        )}
      </Formik>
    </FormContext.Provider>
  );
}

function SubmitOnEnter() {
  const { submitForm, setSubmitting } = useFormikContext();

  useEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      setSubmitting(true);
      submitForm();
    }
  });

  return null;
}

Form.TextareaAutosize = withForm(TextareaAutosize) as any;
Form.TextEditor = withForm(TextEditor);
Form.TextInput = withForm<TextInputProps>(TextInput) as any;
Form.FileInput = withForm(FileInput) as any;
Form.PSPDF = withForm(PSPDFKit) as any;
Form.Option = withForm(Option) as any;
Form.OptionGroup = withForm(OptionGroup) as any;
Form.SubmitButton = SubmitButton;
Form.Button = FormButton;
Form.Header = Header;

export default Form;
